import React, { useState, useEffect, useCallback } from "react";
import { API_URL } from "../config";
import "./AdminRoles.css";

function getToken() {
  return localStorage.getItem("token") || "";
}

const PERMISSION_TREE = [
  {
    category: "Key Frames",
    items: [
      { id: "tools.keyframes", label: "Prompt IN / Prompter" },
      { id: "tools.mdv", label: "MDV" },
      { id: "tools.editor", label: "Editor Key Frame" },
    ],
  },
  {
    category: "Personajes",
    items: [
      { id: "tools.characters", label: "Crea Personaje / Hoja" },
      { id: "tools.wardrobe", label: "Crear Ropa" },
      { id: "tools.scenes", label: "Crea Escena" },
    ],
  },
  {
    category: "Video",
    items: [
      { id: "tools.video", label: "Genera Video" },
      { id: "tools.videoset", label: "Video Set" },
    ],
  },
  {
    category: "Studio",
    items: [
      { id: "studio.character", label: "Personaje Base" },
      { id: "studio.details", label: "Detalles" },
      { id: "studio.character-sheet", label: "Hoja Personaje" },
      { id: "studio.scene", label: "Escenario" },
      { id: "studio.shot", label: "Imagen de Toma" },
      { id: "studio.video", label: "Video-Toma" },
    ],
  },
  {
    category: "Navegacion",
    items: [
      { id: "home", label: "Home" },
      { id: "biblioteca", label: "Biblioteca" },
      { id: "videoteca", label: "Videoteca" },
      { id: "certificar", label: "Certificar" },
    ],
  },
  {
    category: "Admin",
    items: [
      { id: "admin.users", label: "Gestion Usuarios" },
      { id: "admin.roles", label: "Gestion Roles" },
      { id: "admin.news", label: "Gestion Noticias" },
      { id: "see.all.images", label: "Ver todas las imagenes" },
    ],
  },
  {
    category: "Feedback",
    items: [
      { id: "feedback.write", label: "Escribir" },
      { id: "feedback.edit.own", label: "Editar propios" },
      { id: "feedback.delete.own", label: "Eliminar propios" },
    ],
  },
];

export default function AdminRoles() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editPerms, setEditPerms] = useState(new Set());
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newRole, setNewRole] = useState({ id: "", description: "" });
  const [msg, setMsg] = useState("");

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${getToken()}`,
  };

  const loadRoles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/admin/roles`, { headers });
      if (res.ok) setRoles(await res.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRoles(); }, [loadRoles]);

  const selectRole = (role) => {
    setSelected(role.id);
    setEditPerms(new Set(role.permissions || []));
    setDirty(false);
    setMsg("");
  };

  const togglePerm = (permId) => {
    if (selected === "owner") return;
    setEditPerms((prev) => {
      const next = new Set(prev);
      if (next.has(permId)) next.delete(permId);
      else next.add(permId);
      return next;
    });
    setDirty(true);
  };

  const toggleCategory = (cat) => {
    if (selected === "owner") return;
    const catPerms = cat.items.map((i) => i.id);
    const allChecked = catPerms.every((p) => editPerms.has(p));
    setEditPerms((prev) => {
      const next = new Set(prev);
      catPerms.forEach((p) => {
        if (allChecked) next.delete(p);
        else next.add(p);
      });
      return next;
    });
    setDirty(true);
  };

  const saveRole = async () => {
    if (!selected) return;
    setSaving(true);
    setMsg("");
    try {
      const res = await fetch(
        `${API_URL}/admin/roles/${encodeURIComponent(selected)}`,
        { method: "PUT", headers, body: JSON.stringify({ permissions: [...editPerms] }) }
      );
      if (res.ok) {
        setDirty(false);
        setMsg("Guardado");
        loadRoles();
      } else {
        const data = await res.json();
        setMsg(data.detail || "Error al guardar");
      }
    } finally {
      setSaving(false);
    }
  };

  const createRole = async () => {
    if (!newRole.id) { setMsg("El ID del rol es obligatorio"); return; }
    setSaving(true);
    setMsg("");
    try {
      const res = await fetch(`${API_URL}/admin/roles`, {
        method: "POST",
        headers,
        body: JSON.stringify({ id: newRole.id.toLowerCase().trim(), description: newRole.description, permissions: [] }),
      });
      if (res.ok) {
        setShowNew(false);
        setNewRole({ id: "", description: "" });
        loadRoles();
      } else {
        const data = await res.json();
        setMsg(data.detail || "Error al crear rol");
      }
    } finally {
      setSaving(false);
    }
  };

  const deleteRole = async (roleId) => {
    if (!window.confirm(`Eliminar el rol "${roleId}"?`)) return;
    try {
      const res = await fetch(
        `${API_URL}/admin/roles/${encodeURIComponent(roleId)}`,
        { method: "DELETE", headers }
      );
      if (res.ok) {
        if (selected === roleId) { setSelected(null); setEditPerms(new Set()); }
        loadRoles();
      } else {
        const data = await res.json();
        setMsg(data.detail || "Error al eliminar");
      }
    } catch { /* ignore */ }
  };

  const selectedRole = roles.find((r) => r.id === selected);
  const isOwner = selected === "owner";

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h2>Gestion de Roles</h2>
        <button className="admin-btn-new" onClick={() => setShowNew(!showNew)}>
          {showNew ? "Cancelar" : "+ Nuevo rol"}
        </button>
      </div>

      {showNew && (
        <div className="admin-new-form">
          <input placeholder="ID del rol (ej: colaborador)" value={newRole.id} onChange={(e) => setNewRole({ ...newRole, id: e.target.value })} />
          <input placeholder="Descripcion" value={newRole.description} onChange={(e) => setNewRole({ ...newRole, description: e.target.value })} style={{ flex: 2 }} />
          <button className="admin-btn-save" onClick={createRole} disabled={saving}>
            {saving ? "Creando..." : "Crear"}
          </button>
          {msg && !selected && <span className="admin-msg">{msg}</span>}
        </div>
      )}

      {loading ? (
        <div className="admin-loading">Cargando...</div>
      ) : (
        <div className="roles-layout">
          {/* ── Left panel: Role list ── */}
          <div className="roles-list">
            {roles.map((r) => (
              <div
                key={r.id}
                className={`roles-item ${selected === r.id ? "roles-item--active" : ""}`}
                onClick={() => selectRole(r)}
              >
                <div className="roles-item-header">
                  <span className="roles-item-name">{r.id}</span>
                  {!r.is_system && (
                    <button
                      className="roles-item-delete"
                      onClick={(e) => { e.stopPropagation(); deleteRole(r.id); }}
                      title="Eliminar rol"
                    >
                      ✕
                    </button>
                  )}
                </div>
                {r.description && <div className="roles-item-desc">{r.description}</div>}
                <div className="roles-item-count">
                  {r.permissions?.includes("*")
                    ? "Todos los permisos"
                    : `${(r.permissions || []).length} permisos`}
                </div>
              </div>
            ))}
          </div>

          {/* ── Right panel: Permission editor ── */}
          <div className="roles-editor">
            {!selected ? (
              <div className="roles-editor-empty">Selecciona un rol para editar sus permisos</div>
            ) : (
              <>
                <div className="roles-editor-header">
                  <h3>{selected}</h3>
                  {selectedRole?.description && (
                    <span className="roles-editor-desc">{selectedRole.description}</span>
                  )}
                  {isOwner && <span className="roles-owner-badge">Acceso total (*)</span>}
                </div>

                {isOwner ? (
                  <div className="roles-editor-info">
                    El rol owner tiene acceso total a todas las funciones. No se puede modificar.
                  </div>
                ) : (
                  <>
                    <div className="roles-perm-tree">
                      {PERMISSION_TREE.map((cat) => {
                        const catPerms = cat.items.map((i) => i.id);
                        const allChecked = catPerms.every((p) => editPerms.has(p));
                        const someChecked = catPerms.some((p) => editPerms.has(p));
                        return (
                          <div key={cat.category} className="roles-perm-group">
                            <label className="roles-perm-cat">
                              <input
                                type="checkbox"
                                checked={allChecked}
                                ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked; }}
                                onChange={() => toggleCategory(cat)}
                              />
                              {cat.category}
                            </label>
                            <div className="roles-perm-items">
                              {cat.items.map((item) => (
                                <label key={item.id} className="roles-perm-item">
                                  <input
                                    type="checkbox"
                                    checked={editPerms.has(item.id)}
                                    onChange={() => togglePerm(item.id)}
                                  />
                                  <span className="roles-perm-label">{item.label}</span>
                                  <span className="roles-perm-id">{item.id}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="roles-editor-actions">
                      <button className="admin-btn-save" onClick={saveRole} disabled={!dirty || saving}>
                        {saving ? "Guardando..." : "Guardar"}
                      </button>
                      {dirty && (
                        <button className="roles-btn-cancel" onClick={() => selectRole(selectedRole)}>
                          Cancelar
                        </button>
                      )}
                      {msg && selected && (
                        <span className={msg === "Guardado" ? "roles-msg-ok" : "admin-msg"}>{msg}</span>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
